import React from "react";
import { Check, X, Stethoscope, AlertTriangle } from "lucide-react";

/**
 * Generic per-step diagnostic checklist used by:
 *   - Music Studio (Suno wrapper connection)
 *   - AI Provider Center (any BYOK provider)
 *
 * Props:
 *   result: { ok, summary, steps: [{ name, ok, detail }] }
 *   labels: { [step_name]: human_label }
 *   onClose: () => void
 *   footer?: ReactNode — optional extra controls (e.g. Auto-fix button)
 *   testid?: string  — root data-testid, defaults to "diagnostics-panel"
 */
export default function DiagnosticsPanel({ result, labels = {}, onClose, footer, testid = "diagnostics-panel" }) {
  const steps = Array.isArray(result?.steps) ? result.steps : [];
  return (
    <div
      className="mt-3 rounded-md border border-cyan-500/30 bg-cyan-500/5 p-3"
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-cyan-300">
          <Stethoscope className="h-3 w-3" /> Connection check
        </div>
        {onClose && (
          <button onClick={onClose} data-testid={`${testid}-close`} className="text-ink-muted hover:text-ink-primary">
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
      <div className="space-y-1.5">
        {steps.map((s, i) => (
          <div
            key={i}
            data-testid={`diag-step-${s.name}`}
            data-ok={s.ok ? "true" : "false"}
            className="flex items-start gap-2 text-[11px]"
          >
            {s.ok ? (
              <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0 mt-px" />
            ) : (
              <X className="h-3.5 w-3.5 text-brand-onair shrink-0 mt-px" />
            )}
            <div className="min-w-0 flex-1">
              <div className={s.ok ? "text-emerald-200" : "text-brand-onair"}>
                {labels[s.name] || s.name}
              </div>
              {s.detail && (
                <div className="text-[10px] text-ink-muted break-all leading-snug">{s.detail}</div>
              )}
            </div>
          </div>
        ))}
      </div>
      {footer}
      {result?.summary && (
        <div
          className={`mt-2 pt-2 border-t border-cyan-500/20 text-[10px] flex items-start gap-1.5 ${
            result.ok ? "text-emerald-300" : "text-yellow-300"
          }`}
          data-testid={`${testid}-summary`}
        >
          {!result.ok && <AlertTriangle className="h-3 w-3 shrink-0 mt-px" />}
          <span className="leading-snug">{result.summary}</span>
        </div>
      )}
    </div>
  );
}
